import { formatPrice } from '@/common/constant';
import type { WineRecommendation } from '@/common/generated/wine-dish-recommendation.typebox';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { EMPTY_IMAGE_FALLBACK } from '@/lib/constants';
import { Badge, Card, Image, Rate, Tag, Tooltip } from 'antd';
import { getPairingStyleColor } from '../utils';

const getMatchColor = (confidence: number): string => {
  const percentage = Math.round(confidence * 100);
  if (percentage >= 90) return 'red';
  if (percentage >= 75) return 'purple';
  if (percentage >= 60) return 'blue';
  return 'gray';
};

const getRankColor = (rank: number): string => {
  switch (rank) {
    case 1:
      return 'bg-red-600';
    case 2:
      return 'bg-purple-600';
    case 3:
      return 'bg-blue-600';
    default:
      return 'bg-orange-500';
  }
};

interface WineRecommendationCardProps {
  wine: WineRecommendation;
  rank: number;
  onClick: () => void;
}

export function WineRecommendationCard({
  wine,
  rank,
  onClick,
}: WineRecommendationCardProps) {
  const { currency } = useCurrencySymbol();

  return (
    <Badge.Ribbon
      text={`${Math.round(wine.confidence * 100)}% Match`}
      color={getMatchColor(wine.confidence)}
    >
      <Card
        size="small"
        className="relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
        onClick={onClick}
      >
        <div
          className={`absolute top-2 left-2 z-10 ${getRankColor(rank)} text-white text-xs font-bold px-2 py-1 rounded-md`}
        >
          #{rank}
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-center">
            <Image
              src={wine.productImageUrl}
              alt={wine.name}
              width={240}
              height={240}
              className="object-contain"
              preview={false}
              placeholder={true}
              fallback={EMPTY_IMAGE_FALLBACK}
            />
          </div>
          <div className="space-y-2">
            <div>
              <h3 className="font-medium text-base">{wine.name}</h3>
              <div className="text-sm text-gray-500">{wine.region}</div>
            </div>
            <div className="flex items-center justify-between">
              <Tooltip title={(wine.totalPairingScore ?? 'N/A').toString()}>
                <Rate
                  disabled
                  value={
                    wine.totalPairingScore
                      ? Math.ceil(wine.totalPairingScore / 20)
                      : 0
                  }
                  className="text-sm"
                />
              </Tooltip>
              <span className="font-medium">
                {formatPrice(wine.price, currency)}
              </span>
            </div>
            <div className="text-sm text-gray-600">
              {wine.type} • {wine.varietal}
            </div>
          </div>
          <div className="text-sm text-gray-600">
            {wine.characteristics.body} Body • {wine.characteristics.acidity}{' '}
            Acidity
            {wine.characteristics.tannins &&
              ` • ${wine.characteristics.tannins} Tannins`}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Tag
              color={getPairingStyleColor(wine.pairingRationale.pairingStyle)}
              className="text-sm font-medium px-3 py-1"
            >
              {wine.pairingRationale.pairingStyle.charAt(0).toUpperCase() +
                wine.pairingRationale.pairingStyle.slice(1)}{' '}
              Pairing
            </Tag>
          </div>
        </div>
      </Card>
    </Badge.Ribbon>
  );
}
